/**
 * [T270-PY] DayOfWeekCategoryStackChart — Phase C stacked-bar chart for the
 * redesigned Day-of-Week tab on `/transactions`.
 *
 * Contract authority: `docs/superpowers/specs/2026-08-03-day-of-week-insights-design.md`
 * and the registered Phase C selectors appended to
 * `testify/e2e/t270-dayofweek-redesign.spec.ts` (AC-I12..AC-I17).
 *
 * The chart is a real SVG rendered with React. Each day column is a focusable
 * `<g>` that owns a stack of `<rect>` segments — one per category in the
 * `rows` array. Stacks are scaled by the per-day total against the busiest
 * day so each day's contribution is visible while the y-axis stays in IDR
 * currency units via the injected `fmt`.
 *
 * Invariants (Phase C):
 *   - 7 day groups, exactly one per `days` entry, in caller order (Mon-Sun
 *     `2,3,4,5,6,7,1`).
 *   - 4 zero-based y-axis ticks (`0`, `1/3`, `2/3`, `1` of the max day total)
 *     formatted through `fmt`.
 *   - Tooltip stays in DOM (no conditional unmount) and toggles `aria-hidden`
 *     on `mouseenter` / `mouseleave` / focus / blur.
 *   - Wrapper is `w-full min-w-0 overflow-hidden` so the chart scales inside
 *     mobile-first cards without horizontal scroll.
 *
 * @module transactions/DayOfWeekCategoryStackChart
 */
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

import type {
  DayOfWeekHeatmapCategoryRow,
  DayOfWeekHeatmapDay,
} from './DayOfWeekHeatmap';

/** Stable prefix — every Phase C selector is anchored here. */
const TESTID_PREFIX = 'day-of-week-redesign-stack';

/** Minimum focus footprint per day column (WCAG touch target floor). */
const MIN_COLUMN_WIDTH = 44;

/** SVG geometry in CSS pixels. */
const SVG_HEIGHT = 240;
const LEFT_PAD = 64; // room for `fmt`-formatted tick labels
const RIGHT_PAD = 12;
const TOP_PAD = 12;
const BOTTOM_PAD = 40; // day label + axis label
const Y_TICK_COUNT = 4;

/**
 * Deterministic, theme-friendly categorical palette. Indexed by row position
 * so re-ordering the rows changes the visual mapping (the chart is informative,
 * not a drill-through control). Every segment also carries a stable
 * `cat-{sanitizedCategoryId}` class so QA can target segments by category.
 */
const PALETTE: readonly string[] = [
  '#2563eb', // blue-600
  '#059669', // emerald-600
  '#d97706', // amber-600
  '#e11d48', // rose-600
  '#4f46e5', // indigo-600
  '#0d9488', // teal-600
  '#ea580c', // orange-600
  '#db2777', // pink-600
  '#65a30d', // lime-600
  '#0284c7', // sky-600
];

/**
 * Stable, CSS-safe id derived from a categoryId. The `__other__` synthetic
 * bucket and any free-form user category ids collapse into a deterministic
 * slug that QA can reference as `cat-{slug}`.
 */
function sanitizeCategoryId(id: string): string {
  const trimmed = id.replace(/[^a-zA-Z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
  return trimmed.length > 0 ? trimmed : 'uncategorized';
}

function colorForRow(rowIdx: number): string {
  return PALETTE[rowIdx % PALETTE.length] as string;
}

export interface DayOfWeekCategoryStackChartProps {
  /** 7-entry day array in the order the caller wants columns rendered. */
  days: DayOfWeekHeatmapDay[];
  /** One entry per category row (from `buildCategoryRows`). */
  rows: DayOfWeekHeatmapCategoryRow[];
  /** Currency formatter from `useCurrency().fmt`. */
  fmt: (n: number) => string;
}

interface TooltipSegment {
  categoryId: string;
  name: string;
  emoji: string;
  amount: number;
  /** Percentage of the selected day's total — `0..100`. */
  percentage: number;
}

interface TooltipState {
  open: boolean;
  dayIdx: number;
  dayName: string;
  dayTotal: number;
  segments: TooltipSegment[];
}

const HIDDEN_TOOLTIP: TooltipState = {
  open: false,
  dayIdx: 0,
  dayName: '',
  dayTotal: 0,
  segments: [],
};

export function DayOfWeekCategoryStackChart({
  days,
  rows,
  fmt,
}: DayOfWeekCategoryStackChartProps) {
  // 7 day columns max. Fall back to empty arrays so the renderer never throws
  // on a transient mid-load state where the host clears props.
  const safeDays = useMemo<DayOfWeekHeatmapDay[]>(() => {
    if (days.length === 7) return days;
    if (days.length > 0) return days;
    const defaultNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return [2, 3, 4, 5, 6, 7, 1].map((dow, idx) => ({
      dayOfWeek: dow,
      dayName: defaultNames[idx] ?? `Day ${idx + 1}`,
    }));
  }, [days]);


  // Per-day totals across all rows — drives stack heights and y-axis max.
  const perDayTotals = useMemo<number[]>(() => {
    return safeDays.map((_, dayIdx) =>
      rows.reduce(
        (acc, r) => acc + (r.perDayTotals[dayIdx] ?? 0),
        0,
      ),
    );
  }, [safeDays, rows]);

  const maxDayTotal = useMemo<number>(
    () => perDayTotals.reduce((acc, v) => (v > acc ? v : acc), 0),
    [perDayTotals],
  );

  // Responsive layout — measure container so columns stay ≥ MIN_COLUMN_WIDTH.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(640);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => {
      // `clientWidth` is undefined during SSR + first paint on some browsers;
      // `getBoundingClientRect` is also reliable here.
      const rect = el.getBoundingClientRect();
      const width = rect.width > 0 ? rect.width : el.clientWidth;
      if (width > 0) setContainerWidth(width);
    };
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    return undefined;
  }, []);

  const N = safeDays.length;
  const innerWidth = Math.max(0, containerWidth - LEFT_PAD - RIGHT_PAD);
  const requestedColWidth = N > 0 ? innerWidth / N : MIN_COLUMN_WIDTH;
  const colWidth = Math.max(MIN_COLUMN_WIDTH, requestedColWidth);
  const chartWidthPx = LEFT_PAD + colWidth * N + RIGHT_PAD;
  const chartHeightPx = SVG_HEIGHT;
  const innerHeight = Math.max(0, chartHeightPx - TOP_PAD - BOTTOM_PAD);

  // y-axis ticks: zero + 3 evenly-spaced positive markers (≥ 4 total).
  const yTicks = useMemo(() => {
    const ticks: Array<{
      index: number;
      value: number;
      y: number;
      label: string;
    }> = [];
    for (let i = 0; i < Y_TICK_COUNT; i += 1) {
      const fraction = i / (Y_TICK_COUNT - 1);
      const value = maxDayTotal * fraction;
      const y = TOP_PAD + innerHeight - fraction * innerHeight;
      // First tick (zero baseline) must be the literal string "0" so the
      // AC-I16 first-tick regex (`/^0(\s|$)/`) matches the text content.
      const label = i === 0 ? '0' : fmt(value);
      ticks.push({ index: i, value, y, label });
    }
    return ticks;
  }, [maxDayTotal, innerHeight, fmt]);

  // Tooltip state — always in DOM, only `aria-hidden` + `hidden` toggle.
  const [tooltip, setTooltip] = useState<TooltipState>(HIDDEN_TOOLTIP);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ left: number; top: number } | null>(null);

  const showTooltip = useCallback(
    (dayIdx: number) => {
      const day = safeDays[dayIdx];
      if (!day) return;
      const dayTotal = perDayTotals[dayIdx] ?? 0;
      const segments: TooltipSegment[] = rows
        .map((r) => {
          const amount = r.perDayTotals[dayIdx] ?? 0;
          if (amount <= 0) return null;
          return {
            categoryId: r.categoryId,
            name: r.name,
            emoji: r.emoji,
            amount,
            percentage:
              dayTotal > 0 ? (amount / dayTotal) * 100 : 0,
          };
        })
        .filter((s): s is TooltipSegment => s !== null)
        .sort((a, b) => b.amount - a.amount);
      setTooltip({
        open: true,
        dayIdx,
        dayName: day.dayName,
        dayTotal,
        segments,
      });
    },
    [safeDays, perDayTotals, rows],
  );

  const hideTooltip = useCallback(() => {
    setTooltip(HIDDEN_TOOLTIP);
  }, []);

  useLayoutEffect(() => {
    if (!tooltip.open) {
      setTooltipPosition(null);
      return undefined;
    }
    const updatePosition = () => {
      const day = safeDays[tooltip.dayIdx];
      const anchor = day
        ? document.querySelector<SVGGElement>(`[data-testid="${TESTID_PREFIX}-day-${day.dayOfWeek}"]`)
        : null;
      const popup = tooltipRef.current;
      if (!anchor || !popup) return;
      const anchorRect = anchor.getBoundingClientRect();
      const popupWidth = popup.offsetWidth || 260;
      const popupHeight = popup.offsetHeight || 160;
      const floatingBar = document.querySelector('[data-testid="unified-topbar"]')?.getBoundingClientRect();
      const floatingChevron = document.querySelector('[data-testid="floating-bar-collapse-toggle"]')?.getBoundingClientRect();
      const forbiddenBottom = Math.max(floatingBar?.bottom ?? 0, floatingChevron?.bottom ?? 0);
      const left = Math.max(8, Math.min(window.innerWidth - popupWidth - 8, anchorRect.left + anchorRect.width / 2 - popupWidth / 2));
      const preferredTop = anchorRect.top - popupHeight - 8;
      const belowTop = Math.max(anchorRect.bottom + 8, forbiddenBottom + 8);
      let top = preferredTop >= forbiddenBottom + 8 ? preferredTop : belowTop;
      if (top + popupHeight > window.innerHeight - 8) {
        top = Math.max(forbiddenBottom + 8, window.innerHeight - popupHeight - 8);
      }
      setTooltipPosition({ left, top });
    };
    const frame = requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [safeDays, tooltip]);

  // Stable ids for aria-describedby wiring (added by SSR-friendly useId()).
  const reactId = useId();
  const tooltipDescribedById = `${TESTID_PREFIX}-tooltip-desc-${reactId}`;

  const tooltipOverlay = (
    <div
      ref={tooltipRef}
      data-testid={`${TESTID_PREFIX}-tooltip`}
      role="tooltip"
      aria-hidden={tooltip.open ? 'false' : 'true'}
      hidden={!tooltip.open}
      style={{
        position: 'fixed',
        left: tooltipPosition?.left ?? 0,
        top: tooltipPosition?.top ?? 0,
        visibility: tooltipPosition ? 'visible' : 'hidden',
        zIndex: 70,
      }}
      className="pointer-events-none min-w-[160px] max-w-[260px] rounded-md border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md tabular-nums"
    >
      {tooltip.open && (
        <div>
          <div className="mb-1 flex min-w-0 flex-col items-start gap-0">
            <span className="font-semibold text-foreground">{tooltip.dayName}</span>
            <span className="text-left text-muted-foreground" data-role="dow-tooltip-day-total">
              {fmt(tooltip.dayTotal)}
            </span>
          </div>
          {tooltip.segments.length === 0 && <div className="text-muted-foreground">No spending this day</div>}
          {tooltip.segments.map((seg) => (
            <div key={`tooltip-${tooltip.dayIdx}-${seg.categoryId}`} className="flex min-w-0 flex-col items-start gap-0">
              <span className="min-w-0 break-words text-left" data-role="dow-tooltip-segment-name">
                {seg.emoji ? `${seg.emoji} ` : ''}{seg.name}
              </span>
              <span className="flex items-baseline gap-2 text-left" data-role="dow-tooltip-segment-values">
                <span className="shrink-0 text-left text-muted-foreground" data-role="dow-tooltip-segment-percentage">
                  {seg.percentage.toFixed(0)}%
                </span>
                <span className="shrink-0 text-left" data-role="dow-tooltip-segment-amount">{fmt(seg.amount)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
    <div
      ref={containerRef}
      data-testid={`${TESTID_PREFIX}-root`}
      className="relative w-full min-w-0 overflow-hidden"
    >
      <svg
        data-testid={`${TESTID_PREFIX}-chart`}
        role="img"
        aria-label={`Day of week stacked spending chart (IDR) showing category composition across 7 days`}
        aria-describedby={tooltipDescribedById}
        width="100%"
        height={chartHeightPx}
        viewBox={`0 0 ${chartWidthPx} ${chartHeightPx}`}
        preserveAspectRatio="xMidYMid meet"
        className={cn('block h-auto w-full text-foreground')}
      >
        <title>Day of week stacked spending chart (IDR)</title>
        <desc id={tooltipDescribedById}>
          Stacked bar chart of category totals for each of the 7 days of the
          week. Hover or focus a day to view per-category amounts in IDR.
        </desc>

        {/* Y-axis grid + tick labels */}
        <g aria-hidden="true">
          {yTicks.map((tick) => {
            const isBaseline = tick.index === 0;
            return (
              <g key={`ytick-${tick.index}`}>
                <line
                  x1={LEFT_PAD}
                  x2={chartWidthPx - RIGHT_PAD}
                  y1={tick.y}
                  y2={tick.y}
                  className="stroke-border"
                  strokeWidth={isBaseline ? 1.25 : 0.75}
                  strokeDasharray={isBaseline ? undefined : '3 3'}
                />
                <text
                  data-testid={`${TESTID_PREFIX}-y-tick-${tick.index}`}
                  x={LEFT_PAD - 6}
                  y={tick.y + 4}
                  textAnchor="end"
                  className="fill-muted-foreground text-[10px] tabular-nums"
                >
                  {tick.label}
                </text>
              </g>
            );
          })}
        </g>

        {/* X-axis label */}
        <text
          x={chartWidthPx / 2}
          y={chartHeightPx - 6}
          textAnchor="middle"
          className="fill-muted-foreground text-[10px] uppercase tracking-wider"
        >
          day
        </text>

        {/* Day columns — one focusable <g> per days entry */}
        {safeDays.map((day, dayIdx) => {
          const x = LEFT_PAD + colWidth * dayIdx;
          const dayTotal = perDayTotals[dayIdx] ?? 0;
          const dayStackHeight =
            maxDayTotal > 0 ? (dayTotal / maxDayTotal) * innerHeight : 0;
          const stackBaseY = TOP_PAD + innerHeight;
          const dayLabel =
            day.dayName && day.dayName.length > 0
              ? day.dayName
              : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][dayIdx] ??
                `Day ${dayIdx + 1}`;
          return (
            <g
              key={`day-${day.dayOfWeek}`}
              data-testid={`${TESTID_PREFIX}-day-${day.dayOfWeek}`}
              tabIndex={0}
              role="group"
              aria-label={`Stacked spending for ${dayLabel}, ${fmt(dayTotal)} across ${rows.length} categories. Activate to view detail.`}
              onMouseEnter={() => showTooltip(dayIdx)}
              onMouseLeave={hideTooltip}
              onFocus={() => showTooltip(dayIdx)}
              onBlur={hideTooltip}
              className={cn(
                'cursor-pointer outline-none',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
              )}
            >
              {/* Invisible hit rect: ensures the full column is keyboard-focusable
                 at MIN_COLUMN_WIDTH even when the visible bars are narrower. */}
              <rect
                x={x}
                y={TOP_PAD}
                width={colWidth}
                height={innerHeight + BOTTOM_PAD - 8}
                fill="transparent"
                pointerEvents="all"
              />
              {/* X-axis day label (short form) */}
              <text
                x={x + colWidth / 2}
                y={TOP_PAD + innerHeight + 16}
                textAnchor="middle"
                className={cn(
                  'fill-muted-foreground text-[11px]',
                  day.dayOfWeek >= 6
                    ? 'font-medium text-foreground'
                    : '',
                )}
              >
                {dayLabel.slice(0, 3)}
              </text>

              {/* Stacked segments for this day */}
              {(() => {
                let accY = stackBaseY;
                return rows.map((row, rowIdx) => {
                  const amount = row.perDayTotals[dayIdx] ?? 0;
                  if (amount <= 0) return null;
                  if (dayStackHeight <= 0 || dayTotal <= 0) return null;
                  const segHeight =
                    (amount / dayTotal) * dayStackHeight;
                  if (segHeight <= 0) return null;
                  const segY = accY - segHeight;
                  accY = segY;
                  const color = colorForRow(rowIdx);
                  const segId = sanitizeCategoryId(row.categoryId);
                  const segWidth = Math.max(0, colWidth - 4);
                  return (
                    <rect
                      key={`seg-${day.dayOfWeek}-${row.categoryId}`}
                      x={x + 2}
                      y={segY}
                      width={segWidth}
                      height={segHeight}
                      fill={color}
                      stroke="hsl(var(--card))"
                      strokeWidth={0.5}
                      data-category-id={row.categoryId}
                      data-amount={amount}
                      className={`cat-${segId} transition-opacity hover:opacity-90`}
                    >
                      <title>{`${row.name}: ${fmt(amount)}`}</title>
                    </rect>
                  );
                });
              })()}
            </g>
          );
        })}
      </svg>

      {/* Legend — one entry per row, swatch + emoji + name. */}
      <div
        data-testid={`${TESTID_PREFIX}-legend`}
        className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground"
        role="list"
      >
        {rows.map((row, rowIdx) => {
          const segId = sanitizeCategoryId(row.categoryId);
          const color = colorForRow(rowIdx);
          return (
            <div
              key={`legend-${row.categoryId}`}
              data-testid={`${TESTID_PREFIX}-legend-${segId}`}
              className="flex min-h-[24px] items-center gap-1.5"
              role="listitem"
            >
              <span
                aria-hidden="true"
                className="inline-block h-3 w-3 shrink-0 rounded-sm border border-border/60"
                style={{ backgroundColor: color }}
              />
              <span className="break-words">
                {row.emoji ? `${row.emoji} ` : ''}
                {row.name}
              </span>
            </div>
          );
        })}
      </div>

    </div>
    {typeof document !== 'undefined' ? createPortal(tooltipOverlay, document.body) : null}
    </>
  );
}

export default DayOfWeekCategoryStackChart;
